import { fetchPestPacLocationDetail } from './api'
import { withPestPacKnockStatus } from './pestPacPin'

export type PestPacHistoryLink = {
  knockStatusLocationId: number
  count: number
}

// One promise per PestPac location, kept after it settles so reopening the same bubble doesn't
// re-hit the detail endpoint -- that call reaches the PestPac API before it reaches our DB
// (ContactLocationService.GetPestPacLocationDetailAsync). Also dedupes concurrent callers.
const cache = new Map<string, Promise<PestPacHistoryLink | null>>()

/**
 * The KnockStatusLocation backing a PestPac location, for a location whose knock status was
 * CLEARED and so no longer carries one on the map tile (SH-1816).
 *
 * Resolved through the PestPac detail endpoint rather than a coordinate lookup: the backend ties
 * the location to its KnockStatusLocation by kept FK first (Lead/Opportunity ExternalLinkId) and
 * only then falls back to coordinates, so it still resolves for a pin whose linked Lead is closed
 * and whose geocode therefore differs from the knock status'.
 */
export function getPestPacHistoryLink(
  locationId: string,
): Promise<PestPacHistoryLink | null> {
  const cached = cache.get(locationId)
  if (cached) return cached

  const request = fetchPestPacLocationDetail(locationId)
    .then(readHistoryLink)
    .catch((error) => {
      // A failed lookup must stay retryable -- caching the rejection would hide the history for
      // the rest of the session.
      cache.delete(locationId)
      throw error
    })

  cache.set(locationId, request)
  return request
}

/** Call after the active status is cleared or re-set: the history count has changed. */
export function invalidatePestPacHistoryLink(locationId: string): void {
  cache.delete(locationId)
}

function readHistoryLink(detail: unknown): PestPacHistoryLink | null {
  // Reuse the pin mapping so the detail -> knock-status shape stays in one place (SH-661).
  const { knockStatusLocationId, knockStatusCount } = withPestPacKnockStatus(
    null,
    detail,
  )

  return typeof knockStatusLocationId === 'number' && knockStatusCount
    ? { knockStatusLocationId, count: knockStatusCount }
    : null
}
